/**
 * Datadog RUM Polyfill
 *
 * This module provides an early polyfill for window.DD_RUM to ensure it's available
 * before the actual Datadog initialization completes. This prevents timing issues
 * where the consumerweb-ads package tries to access DD_RUM before it's ready.
 *
 * The polyfill queues any early actions and replays them once the real Datadog
 * RUM is initialized.
 *
 * @fileoverview Early polyfill to ensure DD_RUM availability for consumerweb-ads
 */

// Declare window interface
declare global {
  interface Window {
    DD_RUM?: any
    __datadogRum?: any
  }
}

// Create a queue to store actions until Datadog is ready
interface QueuedAction {
  name: string
  context?: any
}

const actionQueue: QueuedAction[] = []

// Create a minimal polyfill that queues actions
const datadogPolyfill = {
  addAction: (name: string, context?: any): void => {
    actionQueue.push({ name, context })

    // Also try to send immediately if real DD_RUM is available
    if (window.__datadogRum && window.__datadogRum !== datadogPolyfill) {
      window.__datadogRum.addAction(name, context)
    }
  },
  addError: (error: Error, context?: any): void => {
    if (window.__datadogRum && window.__datadogRum !== datadogPolyfill) {
      window.__datadogRum.addError(error, context)
    }
  },
  addTiming: (name: string, time?: number): void => {
    if (window.__datadogRum && window.__datadogRum !== datadogPolyfill) {
      window.__datadogRum.addTiming(name, time)
    }
  }
}

// Set the polyfill immediately
if (typeof window !== 'undefined' && !window.DD_RUM) {
  window.DD_RUM = datadogPolyfill
  // Only set __datadogRum if it's not already set
  if (!window.__datadogRum) {
    window.__datadogRum = datadogPolyfill
  }
}

// Export the queue so the real Datadog can replay events
export const getQueuedActions = () => actionQueue

// Function to replay queued actions once real Datadog is ready
export const replayQueuedActions = () => {
  if (
    window.DD_RUM &&
    window.DD_RUM !== datadogPolyfill &&
    actionQueue.length > 0
  ) {
    actionQueue.forEach(({ name, context }) => {
      // eslint-disable-next-line no-unused-expressions
      window.DD_RUM?.addAction(name, context)
    })
    // Clear the queue
    actionQueue.length = 0
  }
}
